import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { CardContent } from '@/shared/components/card-content/card-content';
import { DialogModule } from 'primeng/dialog';
import { VisualizadorPdf } from '@/shared/components/visualizador-pdf/visualizador-pdf';

import type { CancelamentoPjDTO, CancelamentoPjResponse } from '@/core/api/sicom-api/servicos/pessoa-juridica/cancelamento-pj/cancelamento-pj.model';
import { CancelamentoPjServicosService } from '@/features/servicos/pessoa-juridica/content/cancelamento-pj-servicos/cancelamento-pj-servicos.service';
import { formatarDataHoraMascara, formatarDataMascara, montarRequestProtocolo, valorTexto, type ProtocoloResumoConsulta } from '@/features/servicos/shared/utils/protocolo-consulta.utils';
import { iconeDaSecao, secaoEhDeDados, subtituloDaSecao } from '@/features/servicos/shared/utils/secao-detalhamento.utils';
import { rotuloBadgeSituacao, situacaoClassePorTexto } from '@/features/servicos/shared/utils/protocolo-rotulos.utils';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
interface CampoVisualizacao {
    label: string;
    chave: keyof CancelamentoPjDTO;
    formatador?: (valor: unknown) => string;
}

interface SecaoVisualizacao {
    titulo: string;
    campos: CampoVisualizacao[];
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-cancelamento-pj-servicos',
    standalone: true,
    imports: [DialogoPadraoDirective, InputTextModule, CardContent, DialogModule, VisualizadorPdf],
    templateUrl: './cancelamento-pj-servicos.html',
    styleUrl: './cancelamento-pj-servicos.scss'
})
export class CancelamentoPjServicos {
    /**
     * Id do controle de um campo, para o `<label for>` apontar para ele. O id junta seção e rótulo
     * porque os rótulos se repetem entre seções — só o rótulo geraria ids duplicados, e o clique no
     * label levaria o foco para o campo da outra seção.
     */
    idDoCampo(tituloDaSecao: string, rotulo: string): string {
        return `${this.emSlug(tituloDaSecao)}-${this.emSlug(rotulo)}`;
    }

    /** Texto reduzido a `a-z0-9-`, para servir de id de elemento. */
    private emSlug(texto: string): string {
        return (texto ?? '')
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '');
    }

    /**
     * Apresentação das seções — ícone, subtítulo e se a seção é de dados — vem do vocabulário
     * compartilhado: o mesmo título tem a mesma cara em todos os detalhamentos.
     */
    protected readonly iconeDaSecao = iconeDaSecao;
    protected readonly subtituloDaSecao = subtituloDaSecao;
    protected readonly secaoEhDeDados = secaoEhDeDados;
    protected readonly situacaoClassePorTexto = situacaoClassePorTexto;
    protected readonly rotuloBadgeSituacao = rotuloBadgeSituacao;

    readonly protocoloSelecionado = input<ProtocoloResumoConsulta | null>(null);
    readonly codigoEstado = input('');

    readonly carregando = signal(false);
    readonly erroConsulta = signal<string | null>(null);
    readonly dados = signal<CancelamentoPjResponse | null>(null);
    readonly exibirDialogAnexo = signal(false);
    readonly blobAnexo = signal<Blob | null>(null);
    readonly nomeArquivoAnexo = signal('anexo-cancelamento-pj.pdf');

    readonly secoes: SecaoVisualizacao[] = [
        {
            titulo: 'Protocolo',
            campos: [
                { label: 'Número Protocolo', chave: 'numeroProtocolo' },
                { label: 'Ano Protocolo', chave: 'anoProtocolo' },
                { label: 'Data/Hora Solicitacao', chave: 'dataHora', formatador: formatarDataHoraMascara },
                { label: 'Situação', chave: 'situacao', formatador: this.formatarSituacao }
            ]
        },
        {
            titulo: 'Diretor Técnico',
            campos: [
                { label: 'Nome', chave: 'nome' },
                { label: 'CPF', chave: 'cpf' },
                { label: 'CRM Diretor Técnico', chave: 'numCrmDt' },
                { label: 'UF Diretor Técnico', chave: 'ufCrmDt' },
                { label: 'Médico Ativo', chave: 'medicoAtivo', formatador: this.formatarSimNao },
                { label: 'Email Diretor Técnico', chave: 'emailDiretorTecnico', formatador: this.formatarEmails }
            ]
        },
        {
            titulo: 'Prestador',
            campos: [
                { label: 'CRM Empresa', chave: 'crmEmpresa' },
                { label: 'UF Empresa', chave: 'ufEmpresa' },
                { label: 'Razão Social', chave: 'razaoSocial' },
                { label: 'Prestador Ativo', chave: 'prestadorAtivo', formatador: this.formatarSimNao },
                { label: 'Email Prestador', chave: 'emailPrestador', formatador: this.formatarEmails }
            ]
        },
        {
            titulo: 'Pagamento',
            campos: [
                { label: 'Valor', chave: 'valor', formatador: this.formatarMoeda },
                { label: 'Data Emissão', chave: 'dataEmissao', formatador: formatarDataMascara },
                { label: 'Data Vencimento', chave: 'dataVencimento', formatador: formatarDataMascara },
                { label: 'Nosso Número', chave: 'nossoNumero' },
                { label: 'Banco', chave: 'banco' },
                { label: 'Número Convênio', chave: 'numeroConvenio' },
                { label: 'Número Documento', chave: 'numeroDocumento' },
                { label: 'Identificador Pagamento', chave: 'identificadorPagamento' }
            ]
        }
    ];

    private readonly destroyRef = inject(DestroyRef);
    private ultimaChaveConsulta = '';

    private readonly servico = inject(CancelamentoPjServicosService);
    private readonly messageService = inject(MessageService);

    constructor() {
        // Recarrega ao mudar protocolo/UF; o guard ultimaChaveConsulta evita repetir a mesma consulta.
        effect(() => {
            this.protocoloSelecionado();
            this.codigoEstado();
            this.carregarDados();
        });
    }

    valorCampo(campo: CampoVisualizacao): string {
        const dados = this.dados();
        if (!dados) {
            return '-';
        }

        const valorBruto = dados[campo.chave];
        if (valorBruto === null || valorBruto === undefined || valorTexto(valorBruto).trim() === '') {
            return '-';
        }

        if (campo.formatador) {
            const valorFormatado = campo.formatador(valorBruto);
            return valorFormatado ? valorFormatado : '-';
        }

        return valorTexto(valorBruto);
    }

    possuiAnexo(): boolean {
        return !!valorTexto(this.dados()?.linkAnexo).trim();
    }

    onVisualizarAnexo(event: Event): void {
        event.preventDefault();
        this.visualizarAnexo(this.dados()?.linkAnexo, 'anexo-cancelamento-pj.pdf');
    }

    fecharDialogAnexo(): void {
        this.exibirDialogAnexo.set(false);
        this.blobAnexo.set(null);
    }

    private carregarDados(): void {
        const request = montarRequestProtocolo(this.protocoloSelecionado(), this.codigoEstado());

        if (!request) {
            this.ultimaChaveConsulta = '';
            this.dados.set(null);
            this.carregando.set(false);
            this.erroConsulta.set(null);
            return;
        }

        const chaveConsulta = `${request.numeroProtocolo}/${request.anoProtocolo}-${request.ufCrm}`;
        if (chaveConsulta === this.ultimaChaveConsulta) {
            return;
        }

        this.ultimaChaveConsulta = chaveConsulta;
        this.carregando.set(true);
        this.erroConsulta.set(null);

        this.servico
            .consultar(request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    this.dados.set(response ?? null);
                    this.carregando.set(false);
                },
                error: () => {
                    this.dados.set(null);
                    this.carregando.set(false);
                    this.erroConsulta.set('Falha ao carregar os dados de cancelamento de pessoa jurídica.');
                }
            });
    }

    private visualizarAnexo(campoAnexo: unknown, nomeArquivo: string): void {
        const idAnexo = this.extrairIdAnexo(campoAnexo);
        if (!idAnexo) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Id do anexo não identificado.' });
            return;
        }

        this.servico
            .visualizarAnexo(idAnexo)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (blob) => {
                    this.blobAnexo.set(blob.type ? blob : new Blob([blob], { type: 'application/pdf' }));
                    this.nomeArquivoAnexo.set(nomeArquivo);
                    this.exibirDialogAnexo.set(true);
                },
                error: () => {
                    // Mensagem fica a cargo do interceptor central; aqui so o estado local.
                    this.blobAnexo.set(null);
                    this.exibirDialogAnexo.set(false);
                }
            });
    }

    private extrairIdAnexo(campoAnexo: unknown): string | null {
        const texto = valorTexto(campoAnexo).trim();
        if (!texto) {
            return null;
        }

        const regexPorCaminho = /\/(?:webservices\/)?baixar-anexo\/([^/?#]+)/i;
        const matchDireto = regexPorCaminho.exec(texto);
        if (matchDireto?.[1]) {
            return decodeURIComponent(matchDireto[1]);
        }

        try {
            const url = new URL(texto, window.location.origin);
            const matchPathname = regexPorCaminho.exec(url.pathname);
            if (matchPathname?.[1]) {
                return decodeURIComponent(matchPathname[1]);
            }

            const idQuery = url.searchParams.get('idAnexo') || url.searchParams.get('id');
            if (idQuery?.trim()) {
                return idQuery.trim();
            }
        } catch {
            // Ignora parsing de URL e segue fallback.
        }

        if (/^[A-Za-z0-9_-]+$/.test(texto)) {
            return texto;
        }

        return null;
    }

    private formatarSituacao(valor: unknown): string {
        const codigo = valorTexto(valor).trim().toUpperCase();
        switch (codigo) {
            case 'A':
                return 'Ativo';
            case 'T':
                return 'Tramitado';
            case 'P':
                return 'Pendência';
            case 'C':
                return 'Cancelado';
            case 'E':
                return 'Encerrado';
            default:
                return valorTexto(valor);
        }
    }

    private formatarSimNao(valor: unknown): string {
        const texto = valorTexto(valor).trim().toUpperCase();
        if (!texto) {
            return '';
        }

        if (['S', 'SIM', 'TRUE', '1'].includes(texto)) {
            return 'Sim';
        }

        if (['N', 'NAO', 'NÃO', 'FALSE', '0'].includes(texto)) {
            return 'Não';
        }

        return valorTexto(valor);
    }

    private formatarMoeda(valor: unknown): string {
        const numero = Number(valor);
        if (Number.isFinite(numero)) {
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(numero);
        }

        const texto = valorTexto(valor).trim();
        if (!texto) {
            return '';
        }

        const normalizado = Number(texto.replace(/\./g, '').replace(',', '.'));
        if (Number.isFinite(normalizado)) {
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(normalizado);
        }

        return texto;
    }

    private formatarEmails(valor: unknown): string {
        const texto = valorTexto(valor).trim();
        if (!texto) {
            return '';
        }

        return texto
            .split(';')
            .map((email) => email.trim())
            .filter((email) => email.length > 0)
            .join('\n');
    }
}
